import type { GridSlot } from '../../../domain/calculators/installments';

/**
 * Returns the short month name (e.g. "Feb", "Mar") for an installment slot,
 * derived in Asia/Dhaka timezone from that row's dueDate. If the row is absent, returns an empty string.
 */
export function getSlotMonthLabel(slot: GridSlot): string {
  if (slot.row?.dueDate) {
    try {
      const d = new Date(slot.row.dueDate);
      if (!isNaN(d.getTime())) {
        return new Intl.DateTimeFormat('en-US', {
          timeZone: 'Asia/Dhaka',
          month: 'short',
        }).format(d);
      }
    } catch {
      // return empty
    }
  }
  return '';
}

/**
 * Returns the 4-digit calendar year (e.g. 2024, 2025) for an installment slot,
 * derived in Asia/Dhaka timezone from that row's dueDate. If the row is absent, returns null.
 */
export function getSlotYear(slot: GridSlot): number | null {
  if (slot.row?.dueDate) {
    try {
      const d = new Date(slot.row.dueDate);
      if (!isNaN(d.getTime())) {
        const yrStr = new Intl.DateTimeFormat('en-US', {
          timeZone: 'Asia/Dhaka',
          year: 'numeric',
        }).format(d);
        const n = Number(yrStr);
        return isNaN(n) ? null : n;
      }
    } catch {
      // return null
    }
  }
  return null;
}

/**
 * Authoritative 3-milestone model (DEVELOPMENT_PLAN.md §6.3 Q7 amendment, 2026-10-14 21:10).
 * Exactly 3 milestones, tenure-derived, zero percentages:
 * 1. End of Year 1 (month 12) -> Eligible for registration
 * 2. End of Year 7 (month 84) -> Guaranteed land registration
 * 3. End of Year 10 (month 120) -> Apartment key handover
 */
export interface MilestoneInfo {
  month: number;
  yearNumber: number;
  key: string;
  labelKey: string;
  defaultLabel: string;
}

export const TENURE_MILESTONES: Record<number, MilestoneInfo> = {
  12: {
    month: 12,
    yearNumber: 1,
    key: 'm12',
    labelKey: 'milestones.m12',
    defaultLabel: 'Eligible for Registration',
  },
  84: {
    month: 84,
    yearNumber: 7,
    key: 'm84',
    labelKey: 'milestones.m84',
    defaultLabel: 'Guaranteed Land Registration',
  },
  120: {
    month: 120,
    yearNumber: 10,
    key: 'm120',
    labelKey: 'milestones.m120',
    defaultLabel: 'Apartment Key Handover',
  },
};

export function getSlotMilestone(monthNumber?: number | null): MilestoneInfo | null {
  if (typeof monthNumber !== 'number') return null;
  return TENURE_MILESTONES[monthNumber] ?? null;
}

export function getYearMilestone(yearNumber: number): MilestoneInfo | null {
  const endMonth = yearNumber * 12;
  return TENURE_MILESTONES[endMonth] ?? null;
}

export interface YearGroup {
  yearNumber: number; // 1..10
  slots: GridSlot[];
  clearedCount: number;
  advanceCount: number;
  dueCount: number;
  delinquentCount: number;
  isComplete: boolean;
  isActiveYear: boolean;
}

/**
 * Group slots by tenure years (10 rows × 12 cells, "Year 1…10") per §6.3 Q8 / §3.1.
 * Year y = months 12(y-1)+1 … 12y, independent of booking calendar month.
 * A year counts as complete ONLY when all 12 months are settled (clearedCount === 12);
 * prepaid (advance) months do not mark a year complete.
 */
export function groupSlotsByYear(slots: GridSlot[]): YearGroup[] {
  const groups: YearGroup[] = [];

  for (let y = 1; y <= 10; y++) {
    const startIdx = (y - 1) * 12;
    const yearSlots = slots.slice(startIdx, startIdx + 12);

    const clearedCount = yearSlots.filter((s) => s.phase === 'cleared').length;
    const advanceCount = yearSlots.filter((s) => s.phase === 'advance').length;
    const dueCount = yearSlots.filter((s) => s.phase === 'due').length;
    const delinquentCount = yearSlots.filter((s) => s.phase === 'delinquent').length;
    // Settled-only completion: only cleared (PAID) installments count toward completion
    const isComplete = clearedCount === yearSlots.length && yearSlots.length > 0;
    const hasActivity = clearedCount + advanceCount + dueCount + delinquentCount > 0;
    const isActiveYear = !isComplete && hasActivity;

    groups.push({
      yearNumber: y,
      slots: yearSlots,
      clearedCount,
      advanceCount,
      dueCount,
      delinquentCount,
      isComplete,
      isActiveYear,
    });
  }

  return groups;
}
